import { LitElement, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';

import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';

import { styles } from '../styles/shared-styles';

@customElement('app-home')
export class AppHome extends LitElement {

  @property() message = 'Audiometer';
  private analyser: AnalyserNode | null = null;
  private dataArray: Uint8Array | null = null;
  private frequencies: number[] = [110, 220, 440, 880];
  private currentFrequencyIndex: number = 0;

  static styles = [
    styles,
    css`
    #welcomeBar {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
    }

    #welcomeCard,
    #infoCard {
      padding: 18px;
      padding-top: 0px;
    }

    sl-card::part(footer) {
      display: flex;
      justify-content: flex-end;
    }

    @media(min-width: 750px) {
      sl-card {
        width: 70vw;
      }
    }

    @media (horizontal-viewport-segments: 2) {
      #welcomeBar {
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
      }

      #welcomeCard {
        margin-right: 64px;
      }
    }
  `];

  firstUpdated () {
    console.log('This is your home page');
    this.shadowRoot?.querySelector('#playNextButton')?.addEventListener('click', () => this.playNextFrequency());

    // Clear heardFrequencies on page refresh
    sessionStorage.removeItem('heardFrequencies');
    sessionStorage.removeItem('unheardFrequencies');

    // Request microphone permission immediately when the page loads
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(() => {
        console.log('Microphone permission granted');
      })
      .catch(err => {
        console.error('Microphone permission denied', err);
      });
  }

  async playNextFrequency () {
    const playNextButton = this.shadowRoot?.querySelector('#playNextButton') as HTMLButtonElement;
    const heardButton = this.shadowRoot?.querySelector('#heardButton') as HTMLButtonElement;

    playNextButton.disabled = true;
    heardButton.disabled = false;

    const frequency = this.frequencies[this.currentFrequencyIndex];
    this.currentFrequencyIndex++;

    const audioContext = new window.AudioContext();
    const oscillator = audioContext.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);

    this.analyser = audioContext.createAnalyser();
    this.analyser.fftSize = 2048;
    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);

    oscillator.connect(audioContext.destination);

    // Generate a random delay such that the sound plays after 1 to 5 seconds
    const randomDelay = 1000 + Math.random() * 4000; // Random delay between 0 and 9 seconds

    // Add event listener for heardButton
    const heardButtonListener = () => {
      const heardFrequencies = JSON.parse(sessionStorage.getItem('heardFrequencies') || '[]');
      if (!heardFrequencies.includes(frequency)) {
        heardFrequencies.push(frequency);
        sessionStorage.setItem('heardFrequencies', JSON.stringify(heardFrequencies));
      }
      heardButton.removeEventListener('click', heardButtonListener);
    };
    heardButton.addEventListener('click', heardButtonListener);

    setTimeout(async () => {
      oscillator.start();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(this.analyser!);

      const draw = () => {
        if (oscillator) {
          requestAnimationFrame(draw);
          this.analyser!.getByteFrequencyData(this.dataArray!);
          sessionStorage.setItem('fftData', JSON.stringify(Array.from(this.dataArray!)));
        }
      };

      draw();

      setTimeout(() => {
        oscillator.stop();

        // Calculate the remaining delay to make the total time 10 seconds
        const remainingDelay = 10000 - randomDelay - 1000;
        setTimeout(() => {
          heardButton.disabled = true;
          playNextButton.disabled = false;
          heardButton.removeEventListener('click', heardButtonListener);

          // If the user did not press the heardButton, add the frequency to unheardFrequencies
          const heardFrequencies = JSON.parse(sessionStorage.getItem('heardFrequencies') || '[]');
          if (!heardFrequencies.includes(frequency)) {
            const unheardFrequencies = JSON.parse(sessionStorage.getItem('unheardFrequencies') || '[]');
            unheardFrequencies.push(frequency);
            sessionStorage.setItem('unheardFrequencies', JSON.stringify(unheardFrequencies));
          }

          // Automatically navigate to the about page if all frequencies have been played
          if (this.currentFrequencyIndex >= this.frequencies.length) {
            window.location.href = resolveRouterPath('about');
          }
        }, remainingDelay);
      }, 1000);
    }, randomDelay);
  }

  share () {
    if ((navigator as any).share) {
      (navigator as any).share({
        title: 'Audiometer',
        text: 'Check out Audiometer!',
        url: 'https://github.com/vajya/audiometer',
      });
    }
  }

  render () {
    return html`
      <style>
        #welcomeCard h2 {
          text-align: center;
        }
        #playNextButton, #heardButton {
          display: block;
          margin-left: auto;
          margin-right: auto;
        }
      </style>
      <app-header></app-header>

      <main>
        <div id="welcomeBar">
          <sl-card id="welcomeCard">
            <div slot="header">
              <h2>${this.message}</h2>
            </div>
            <sl-button id="playNextButton" variant="primary" style="margin-bottom: 10px;">Play Next Frequency</sl-button>
            <br>
            <sl-button id="heardButton" variant="primary" disabled>Heard</sl-button>
            ${'share' in navigator
        ? html`<sl-button slot="footer" variant="default" @click="${this.share}">
                        <sl-icon slot="prefix" name="share"></sl-icon>
                        Share Audiometer!
                      </sl-button>`
        : null}
          </sl-card>
        </div>
      </main>
    `;
  }
}
